import React, {
    ChangeEvent,
    ForwardedRef,
    forwardRef,
    MouseEvent,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState
} from 'react';
import cx from 'classnames';
import {
    Flex,
    getDirectionByText,
    Icon,
    noop,
    PriorityIcon,
    useDropdown
} from '@priority-software/priority-style-react';
import {AutomationFunctionComponent} from '../../../FunctionDialog/Dialog';
import {RuleDialogContext} from '../../../RulesListDialog/context';
import {DropDownV2Context} from '@priority-software/priority-style-react';
import {AutomationChoose} from '../../../../reducer/model';
import {AutomationFieldSearch} from './AutomationFieldSearch';
import {priority} from '@priority-software/priority-proto-api/lib';
import {getInputDirection} from '../../../../../Form/common/getInputDirections';
import {AutomationContext} from '../../../../context';
import {tabsData} from '../../../FunctionDialog/constants';
import {DateControl} from './parts/DateControl/DateControl';
import {FunctionIcon} from './parts/FunctionIcon/FunctionIcon';
import {texts} from '../../../../texts';
import styles from './styles.module.scss';
import {ruleDialogID} from '../../../../constants';
import {OldZoomValues} from '../../enum';
import {AutomationBPMFieldSearch} from './AutomationBPMFieldSearch';

type MultiControlTriggerProps = {
    openFunctionDialog: (e: MouseEvent<HTMLElement>) => void;
    size?: 'small' | 'medium' | 'large';
    value: string;
    onChange: (value: string, name: string) => void;
    placeholder?: string;
    hasDropdown?: boolean;
    name: string;
};

const defaultInputDirection = getInputDirection();

export const MultiControlTrigger = forwardRef(
    (props: MultiControlTriggerProps, ref: ForwardedRef<HTMLInputElement>) => {
        const {
            openFunctionDialog,
            size = 'medium',
            value = '',
            onChange,
            hasDropdown,
            placeholder = '',
            name
        } = props;
        const {isOpen, toggleOpen} = useContext(DropDownV2Context);
        const inputValueDirection = getDirectionByText(value, defaultInputDirection);

        const handleInputValue = useCallback(
            (e: ChangeEvent<HTMLInputElement>) => {
                onChange(e.target.value, name);
            },
            [name, onChange]
        );

        const handleFocus = useCallback(() => {
            if (isOpen) {
                toggleOpen();
            }
        }, [isOpen, toggleOpen]);

        return (
            <div className={styles.multiControlTrigger}>
                <div
                    className={cx(styles.inputWrapper, styles.box, styles.inputWrapperPaddingAll, {
                        [styles.small]: size === 'small',
                        [styles.medium]: size === 'medium',
                        [styles.large]: size === 'large'
                    })}
                >
                    <input
                        ref={ref}
                        type="text"
                        className={cx(styles.input, {
                            [styles.forceLTR]: inputValueDirection === 'ltr',
                            [styles.forceRTL]: inputValueDirection === 'rtl'
                        })}
                        value={value}
                        onChange={handleInputValue}
                        onFocus={handleFocus}
                        placeholder={placeholder}
                    />

                    <Flex inline alignItems="center">
                        {hasDropdown ? (
                            <div onClick={toggleOpen}>
                                <Icon
                                    hoverable
                                    iconColor="primary-700"
                                    icon={isOpen ? PriorityIcon.arrowUp : PriorityIcon.arrowDown}
                                />
                            </div>
                        ) : null}

                        <FunctionIcon openFunctionDialog={openFunctionDialog} />
                    </Flex>
                </div>
            </div>
        );
    }
);

type Props = {
    value: string;
    onChange: (value: string, name: string) => void;
    name: string;
    zoom?: priority.netitems.form.ZoomType | OldZoomValues;
    relatedField: number;
    placeholder?: string;
    openingSide?: 'leftSide' | 'rightSide';
    isBPM?: boolean;
};

export const SingleValueControl = (props: Props) => {
    const {value, onChange, name, relatedField, placeholder, openingSide, zoom, isBPM} = props;

    const {toStatus} = useContext(AutomationContext);
    const {handleEscClosable, session, closeCurrentAutomation} = useContext(RuleDialogContext);
    const [showFunctionDialog, setFunctionDialog] = useState(false);

    const inputRef = useRef(null);

    const {isOpen, closeDropdown, openDropdown} = useDropdown({onClose: noop});

    const openFunctionDialog = useCallback(
        (e: MouseEvent<HTMLElement>) => {
            e.stopPropagation();

            setFunctionDialog(true);
            handleEscClosable(false); // prevent ESC close handler of the RuleDialog
        },
        [handleEscClosable]
    );

    const closeFunctionDialog = useCallback(() => {
        setFunctionDialog(false);
        handleEscClosable(true); // enable ESC close handler of the RuleDialog

        requestAnimationFrame(() => {
            inputRef.current?.focus();
        });
    }, [handleEscClosable]);

    const onFunctionDialogSave = useCallback(
        async (value: string) => {
            onChange(value, name);
            closeFunctionDialog();
        },
        [closeFunctionDialog, onChange, name]
    );

    const onSelect = useCallback(
        (option: AutomationChoose) => {
            onChange(option.value as string, name);
            closeDropdown();
        },
        [onChange, closeDropdown, name]
    );

    const {choosesOptions} = useContext(AutomationContext);
    const {EnglishFields: columnsOptions, ExprPredefined, ExprFunc} = choosesOptions;
    const functionOptionsData = useMemo(() => {
        if (showFunctionDialog) {
            return tabsData(columnsOptions, ExprFunc, ExprPredefined);
        }
        return [];
    }, [columnsOptions, ExprFunc, ExprPredefined, showFunctionDialog]);

    const isDateZoom =
        zoom === OldZoomValues.date ||
        zoom === priority.netitems.form.ZoomType.Date8Zoom ||
        zoom === priority.netitems.form.ZoomType.Date10Zoom ||
        zoom === priority.netitems.form.ZoomType.Date14Zoom;

    const getControlTrigger = (hasDropdown: boolean) => {
        return (
            <MultiControlTrigger
                ref={inputRef}
                value={value}
                name={name}
                onChange={onChange}
                openFunctionDialog={openFunctionDialog}
                placeholder={placeholder}
                hasDropdown={hasDropdown}
            />
        );
    };

    return (
        <div className={styles.functionControl}>
            {isDateZoom ? (
                <DateControl
                    session={session}
                    columnID={relatedField}
                    openFunctionDialog={openFunctionDialog}
                    value={value}
                    onChange={onChange}
                    name={name}
                    onCriticalError={closeCurrentAutomation}
                    zoom={zoom}
                    placeholder={texts.date_control_placeholder}
                />
            ) : isBPM ? (
                <AutomationBPMFieldSearch
                    value={value}
                    isOpen={isOpen}
                    session={session}
                    relatedField={relatedField}
                    openingSide={openingSide}
                    onOpen={openDropdown}
                    onClose={closeDropdown}
                    onSelect={onSelect}
                    onCriticalError={closeCurrentAutomation}
                    portalSelector={ruleDialogID}
                    trigger={getControlTrigger(
                        zoom === OldZoomValues.choose || zoom === OldZoomValues.search
                    )}
                />
            ) : (
                <AutomationFieldSearch
                    value={value}
                    isOpen={isOpen}
                    session={session}
                    relatedField={relatedField}
                    openingSide={openingSide}
                    onOpen={openDropdown}
                    onClose={closeDropdown}
                    onSelect={onSelect}
                    onCriticalError={closeCurrentAutomation}
                    portalSelector={ruleDialogID}
                    trigger={getControlTrigger(!!zoom)}
                />
            )}

            {showFunctionDialog && (
                <AutomationFunctionComponent
                    mainTitle={texts.function_dialog_title}
                    helpText={texts.function_dialog_help}
                    optionsData={functionOptionsData}
                    session={session}
                    relatedField={relatedField}
                    defaultValue={value}
                    onClose={closeFunctionDialog}
                    onSave={onFunctionDialogSave}
                    onCriticalError={closeCurrentAutomation}
                    isBPM={!!toStatus}
                />
            )}
        </div>
    );
};
